import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Account, AccountPosition } from '../types';
import TierChip from './portfolio/TierChip';
import { getTierCatalogEntryForPosition } from '../tiers/resolveTierContext';
import {
  TrendingUp,
  TrendingDown,
  Eye,
  X,
  Minus,
  ChevronDown,
  ChevronUp,
  Bot,
  Lock,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';

interface PositionCardProps {
  position: AccountPosition;
  activityLogData?: Array<{
    timestamp: string;
    totalReturn: number;
    dollarReturn?: number;
  }>;
  aiReviewData?: any;
  currentPrice?: number;
  updating?: string | null;
  onViewDetails: (position: AccountPosition) => void;
  onCloseFull: (position: AccountPosition) => void;
  onCloseHalf: (position: AccountPosition) => void;
  readOnly?: boolean;
  account?: Account;
}

const PositionCard: React.FC<PositionCardProps> = ({
  position,
  activityLogData = [],
  aiReviewData,
  currentPrice: propCurrentPrice,
  updating,
  onViewDetails,
  onCloseFull,
  onCloseHalf,
  readOnly = false,
  account,
}) => {
  const tierEntry = getTierCatalogEntryForPosition(position, account);
  const [isAiReviewExpanded, setIsAiReviewExpanded] = useState(false);
  // Use prop current price (from Schwab API) or fallback to position data
  const currentPrice =
    propCurrentPrice || position.actualPosition?.schwabInstrumentPrice || 0;

  // Get trade direction for icon
  const tradeDirection = position.tradeDirection;
  const isBullish =
    tradeDirection?.toLowerCase().includes('bull') ||
    tradeDirection?.toLowerCase().includes('up') ||
    tradeDirection?.toLowerCase().includes('long');

  // Get latest performance data from activity log
  const latestActivity = activityLogData[activityLogData.length - 1];
  const totalReturnPercent = latestActivity?.totalReturn || 0;
  const dollarReturn = latestActivity?.dollarReturn || 0;

  // Calculate days held
  const daysHeld = position.createdAt
    ? Math.ceil(
        (new Date().getTime() - new Date(position.createdAt).getTime()) /
          (1000 * 60 * 60 * 24),
      )
    : 0;

  // Prepare chart data from activity log
  const chartData =
    activityLogData.length > 0
      ? activityLogData.map((log, index) => {
          // Calculate hours since position creation for x-axis
          const hoursSinceCreation =
            (new Date(log.timestamp).getTime() -
              new Date(position.createdAt).getTime()) /
            (1000 * 60 * 60);
          return {
            day: Math.round(hoursSinceCreation * 10) / 10, // Round to 1 decimal place
            return: log.totalReturn,
            timestamp: log.timestamp,
          };
        })
      : [
          { day: 0, return: 0, timestamp: position.createdAt },
          {
            day: daysHeld * 24, // Convert days to hours for consistency
            return: totalReturnPercent,
            timestamp: new Date().toISOString(),
          },
        ];

  // Debug logging for chart data
  console.log(`📊 Chart data for ${position.symbol}:`, {
    activityLogDataLength: activityLogData.length,
    activityLogData: activityLogData,
    chartData: chartData,
    latestReturn: totalReturnPercent,
    daysHeld: daysHeld,
  });

  const formatCurrency = (value: number) => {
    if (isNaN(value)) return '$0.00';
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentage = (value: number) => {
    if (isNaN(value)) return '0.00%';
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-900">
            {position.symbol}
          </h3>
          {tierEntry ? (
            <TierChip riskTier={tierEntry.riskTier} />
          ) : (
            <TierChip variant="unassigned" />
          )}
          <div className="flex items-center space-x-1">
            {isBullish ? (
              <div title="Bullish">
                <TrendingUp className="w-4 h-4 text-green-500" />
              </div>
            ) : (
              <div title="Bearish">
                <TrendingDown className="w-4 h-4 text-red-500" />
              </div>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-medium text-gray-900">
            {formatCurrency(currentPrice)}
          </div>
          <div className="text-xs text-gray-500">Current Price</div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <div className="text-sm text-gray-500">Total P&L</div>
          <div
            className={`text-lg font-semibold ${dollarReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}
          >
            {formatCurrency(dollarReturn)}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Return %</div>
          <div
            className={`text-lg font-semibold ${totalReturnPercent >= 0 ? 'text-green-600' : 'text-red-600'}`}
          >
            {formatPercentage(totalReturnPercent)}
          </div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Days Held</div>
          <div className="text-lg font-semibold text-gray-900">{daysHeld}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Status</div>
          <div className="text-lg font-semibold text-blue-600">
            {position.status}
          </div>
        </div>
      </div>

      {/* Mini Chart */}
      <div className="h-20 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="day" hide domain={['dataMin', 'dataMax']} />
            <YAxis hide domain={['dataMin - 1', 'dataMax + 1']} />
            <Tooltip
              formatter={(value: number) => [`${value.toFixed(2)}%`, 'Return']}
              labelFormatter={(label) => `${label}h`}
              contentStyle={{
                backgroundColor: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '6px',
                fontSize: '12px',
              }}
            />
            <Line
              type="monotone"
              dataKey="return"
              stroke={totalReturnPercent >= 0 ? '#10b981' : '#ef4444'}
              strokeWidth={2}
              dot={false}
              activeDot={{
                r: 4,
                fill: totalReturnPercent >= 0 ? '#10b981' : '#ef4444',
              }}
            />
            <ReferenceLine
              y={0}
              stroke="#6b7280"
              strokeDasharray="2 2"
              strokeWidth={1}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* AI Review Section */}
      {aiReviewData && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-medium text-blue-900 flex items-center">
              <Bot className="w-4 h-4 mr-1" />
              Latest AI Review
            </h4>
            <span className="text-xs text-blue-600">
              {aiReviewData.createdAt
                ? formatDateTime(aiReviewData.createdAt)
                : 'Recent'}
            </span>
          </div>

          {/* Recommendation and Confidence */}
          {aiReviewData.details?.recommendation && (
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-blue-900">
                  Recommendation:
                </span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    aiReviewData.details.recommendation === 'keep'
                      ? 'bg-green-100 text-green-800'
                      : aiReviewData.details.recommendation === 'close'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                  }`}
                >
                  {aiReviewData.details.recommendation.toUpperCase()}
                </span>
              </div>
              {aiReviewData.details.confidence && (
                <span className="text-xs text-blue-600">
                  {aiReviewData.details.confidence}% confidence
                </span>
              )}
            </div>
          )}

          {/* Expand/Collapse Button */}
          <button
            onClick={() => setIsAiReviewExpanded(!isAiReviewExpanded)}
            className="flex items-center space-x-1 text-xs text-blue-600 hover:text-blue-800 mb-2"
          >
            <span>{isAiReviewExpanded ? 'Hide Details' : 'Show Details'}</span>
            {isAiReviewExpanded ? (
              <ChevronUp className="w-3 h-3" />
            ) : (
              <ChevronDown className="w-3 h-3" />
            )}
          </button>

          {/* Expandable Details */}
          {isAiReviewExpanded && (
            <div className="space-y-2">
              {/* Main Description */}
              <div className="text-sm text-blue-800">
                {aiReviewData.description || 'AI risk assessment completed'}
              </div>

              {/* Market and Social Sentiment */}
              {(aiReviewData.details?.marketSentiment ||
                aiReviewData.details?.socialSentiment) && (
                <div className="text-xs text-blue-700 space-y-1">
                  {aiReviewData.details.marketSentiment && (
                    <div>
                      <span className="font-medium">Market:</span>{' '}
                      {aiReviewData.details.marketSentiment}
                    </div>
                  )}
                  {aiReviewData.details.socialSentiment && (
                    <div>
                      <span className="font-medium">Sentiment:</span>{' '}
                      {aiReviewData.details.socialSentiment}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Quick Actions */}
      {!readOnly && (
        <div className="flex space-x-2">
          <button
            onClick={() => onViewDetails(position)}
            className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Eye className="w-4 h-4 mr-1" />
            View Details
          </button>
          <button
            onClick={() => onCloseHalf(position)}
            disabled={updating === position._id || true}
            className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium text-orange-700 bg-orange-100 border border-orange-300 rounded-md hover:bg-orange-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            <Minus className="w-4 h-4 mr-1" />
            Close Partial (coming soon)
          </button>
          <button
            onClick={() => onCloseFull(position)}
            disabled={updating === position._id}
            className="flex-1 flex items-center justify-center px-3 py-2 text-sm font-medium text-red-700 bg-red-100 border border-red-300 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {updating === position._id ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700 mr-1"></div>
                Closing...
              </>
            ) : (
              <>
                <X className="w-4 h-4 mr-1" />
                Close Full
              </>
            )}
          </button>
        </div>
      )}
      {readOnly && (
        <div className="text-center py-2 text-sm text-gray-500 border-t border-gray-200 mt-4 pt-4">
          <Lock className="w-4 h-4 mx-auto mb-1" />
          Read-only preview - <Link to="/register" className="text-primary-600 hover:underline">Join the Waitlist</Link>.
        </div>
      )}
    </div>
  );
};

export default PositionCard;
